import { useState } from 'react';
import { Loader, RefreshCw, ShieldCheck } from 'lucide-react';
import type { UpdaterAnswer, UpdaterRequest, UpdaterScheduleOption } from '../../types/modpackUpdater';
import { validUpdaterAnswer } from '../../utils/modpackUpdater';
import { provisioningScheduleCounts, scheduleSpacing, updaterScheduleRecovery } from '../../utils/provisioningScheduleRecovery';
import { ProvisioningSchedulePreview } from './ProvisioningSchedulePreview';

function ScheduleChanges({ option }: { option: UpdaterScheduleOption }) {
  const original = provisioningScheduleCounts(option.schedules, true);
  const changes = [
    ['Backup start spacing', scheduleSpacing(3600), scheduleSpacing(option.backup_seconds)],
    ['Restart/Minecraft start spacing', scheduleSpacing(3600), scheduleSpacing(option.restart_start_seconds)],
    ['Backups per day', String(original.backup), String(option.backup_count)],
    ['Restarts/Minecraft starts per day', String(original.restart), String(option.restart_count)],
  ];
  return <ul className="text-xs space-y-1">{changes.map(([label, before, after]) => <li key={label} className={before === after ? 'text-[var(--text-muted)]' : 'text-amber-200'}>
    {label}: {before === after ? `${after} (unchanged)` : `${before} → ${after}`}
  </li>)}</ul>;
}

/** Selection only opens a preview; the separate confirmation submits the exact option ID. */
export function ProvisioningScheduleChoices({ request, selectedId, disabled, onSelect }: {
  request: UpdaterRequest; selectedId: string; disabled: boolean; onSelect: (id: string) => void;
}) {
  const recovery = updaterScheduleRecovery(request);
  const selected = recovery?.options.find(option => option.id === selectedId);
  if (!recovery) return <p role="alert" className="text-sm text-red-300">The schedule preview is incomplete. Refresh the options before choosing a schedule.</p>;
  return <>
    <p className="text-xs text-[var(--text-secondary)]">The requested policy separates backup starts by 60 minutes and, independently, restart and Minecraft starts by 60 minutes. Review any shorter gaps or reduced daily counts below. Confirming keeps this prepared job and its other settings.</p>
    {recovery.options.length ? <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-sm text-[var(--text-secondary)] mb-2">Choose a schedule to preview</legend>
      {recovery.options.map(option => <div key={option.id} className="rounded-lg border border-glass-200/15 p-3 space-y-2">
        <label className="flex gap-3 text-sm text-white cursor-pointer"><input type="radio" name={`schedule-${request.id}`} value={option.id} checked={selectedId === option.id} onChange={() => onSelect(option.id)} className="mt-1 shrink-0 accent-accent-400" />
          <span>{option.label}</span>
        </label>
        <div className="ml-6"><ScheduleChanges option={option} /></div>
      </div>)}
    </fieldset> : <p role="status" className="text-sm text-amber-200">No safe schedule options are currently available. Refresh to check the host again.</p>}
    {selected ? <div className="space-y-3" aria-label="Selected schedule preview">
      <h5 className="text-sm font-semibold text-white">Review selected schedule: {selected.label}</h5>
      <ScheduleChanges option={selected} />
      <ProvisioningSchedulePreview schedules={selected.schedules} />
      <p className="text-xs text-amber-200">Confirm only after reviewing these action times and changes. Installation still requires approval of the completed provisioning plan.</p>
    </div> : !!recovery.options.length && <p className="text-xs text-[var(--text-muted)]">No option selected. Choose one to review its full schedule before confirming.</p>}
  </>;
}

export function ProvisioningScheduleRequest({ request, disabled, onAnswer }: {
  request: UpdaterRequest; disabled: boolean; onAnswer: (id: string, value: UpdaterAnswer) => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const answer = `schedule:${selectedId}`;
  const submit = async (value: string) => {
    if (disabled || sending || !validUpdaterAnswer(request, value)) return;
    setSending(true); setError(null);
    try { await onAnswer(request.id, value); setSelectedId(''); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'The schedule request could not be reached.'); }
    finally { setSending(false); }
  };
  return <section className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 space-y-3" aria-labelledby={`request-${request.id}`}>
    <div className="flex gap-2 items-center"><ShieldCheck className="w-4 h-4 text-amber-300 shrink-0" /><h4 id={`request-${request.id}`} className="font-semibold text-sm text-white">{request.title}</h4></div>
    {!!request.lines.length && <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">{request.lines.join('\n')}</p>}
    <ProvisioningScheduleChoices request={request} selectedId={selectedId} disabled={disabled || sending} onSelect={id => { setSelectedId(id); setError(null); }} />
    {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    <div className="flex flex-wrap justify-end gap-3">
      <button type="button" className="glass-button" disabled={disabled || sending} onClick={() => void submit('schedule:refresh')}><RefreshCw className="w-4 h-4" />Refresh schedule options</button>
      <button type="button" className="btn-primary" disabled={disabled || sending || !validUpdaterAnswer(request, answer)} onClick={() => void submit(answer)}>{sending ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Confirm schedule and continue</button>
    </div>
  </section>;
}
